import { useState } from "react";
import { Affix, Button, Empty, EnvBadge, ErrorNotice, Field, InfoTip, Input, Loading, Modal, Panel, Segmented, Select, useToast } from "../../components/ui";
import { gameApi, type GameApi } from "../../lib/api";
import { formatMoney, parseMoney, type CurrencyLike } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient } from "../../lib/session";
import type { Bank, DepositProduct, DepositProductInput, Env, MaturityInstruction } from "../../lib/types";
import type { GameTabProps } from "./Game";

/** Basis points as a percentage: 350 -> "3.5%". */
export const pct = (bps: number) => `${bps / 100}%`;

/** "3% from ₦0 · 5% from ₦100,000.00 (banded)" */
export function describeRates(p: Pick<DepositProduct, "kind" | "rate_tiers" | "tier_method" | "terms">, cur: CurrencyLike): string {
  if (p.kind === "fixed_deposit") return p.terms.map((t) => `${t.days} days at ${pct(t.rate_bps)}`).join(" · ");
  const tiers = p.rate_tiers.map((t) => (t.from_minor === 0 ? pct(t.rate_bps) : `${pct(t.rate_bps)} from ${formatMoney(t.from_minor, cur)}`)).join(" · ");
  return p.rate_tiers.length > 1 ? `${tiers} (${p.tier_method === "banded" ? "banded" : "whole balance"})` : tiers;
}

/** The product's withdrawal rule in words. */
export function describeWithdrawals(p: Pick<DepositProduct, "withdrawals_per_month" | "excess_withdrawal_action">): string {
  if (p.withdrawals_per_month == null) return "Unlimited withdrawals";
  const then = { block: "then blocked", forfeit_interest: "then the month's interest is lost", fee: "then a fee" }[p.excess_withdrawal_action];
  return `${p.withdrawals_per_month} a month, ${then}`;
}

const INSTRUCTIONS: { value: MaturityInstruction; label: string }[] = [
  { value: "payout", label: "Pay out" },
  { value: "rollover_principal", label: "Roll over the principal" },
  { value: "rollover_all", label: "Roll over everything" },
];

export function GameSavings({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const banks = useQuery(`dev:banks:${game.id}:${env}`, () => api.banks());
  const [selected, setSelected] = useState<string | null>(null);
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === selected) ?? list[0];

  if (banks.loading && !banks.data) return <Loading />;
  if (banks.error) return <ErrorNotice error={banks.error} onRetry={banks.reload} />;
  if (!bank) {
    return (
      <Empty title={`No ${env} banks yet`} action={<Link className="btn primary" to={`/dev/games/${game.id}/banks${env === "production" ? "?env=production" : ""}`}>Create a bank</Link>}>
        Savings products belong to a bank. Create one first.
      </Empty>
    );
  }
  return (
    <div className="stack">
      {list.length > 1 && (
        <Field label="Bank">
          <Select value={bank.id} onChange={(e) => setSelected(e.target.value)} style={{ maxWidth: 360 }}>
            {list.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.bank_code})</option>)}
          </Select>
        </Field>
      )}
      <SavingsSwitch api={api} gameId={game.id} env={env} bank={bank} />
      <Products key={bank.id} api={api} gameId={game.id} env={env} bank={bank} />
    </div>
  );
}

function SavingsSwitch({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const on = bank.feature_flags.savings === true;
  const flip = useAction(() => api.updateBank(bank.id, { feature_flags: { savings: !on } }));
  return (
    <Panel
      title={<>Savings in {bank.name} <EnvBadge env={env} /></>}
      sub="Players earn interest on the game clock: it accrues every game day and is paid monthly or quarterly. Fixed deposits lock money away for a term at a fixed rate."
      actions={
        <Button variant={on ? undefined : "primary"} size="small" busy={flip.pending} onClick={async () => {
          if (await flip.run()) {
            invalidate(`dev:banks:${gameId}:${env}`);
            toast(on ? "Savings turned off: new savings accounts and deposits are refused" : "Savings turned on");
          }
        }}>
          {on ? "Turn savings off" : "Turn savings on"}
        </Button>
      }
    >
      <ErrorNotice error={flip.error} />
      <p className="small muted" style={{ margin: 0 }}>
        {on ? <><span className="badge ok">On</span> Savings accounts and fixed deposits can be opened.</> : <><span className="badge bad">Off</span> Turn savings on, then add products below.</>}
        {" "}Interest is paid from the bank's interest expense account; withholding tax goes to levies payable.
      </p>
    </Panel>
  );
}

function Products({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const key = `dev:deposit-products:${gameId}:${env}:${bank.id}`;
  const products = useQuery(key, () => api.depositProducts(bank.id));
  const [editing, setEditing] = useState<DepositProduct | "savings" | "fixed_deposit" | null>(null);
  const rows = products.data?.data ?? [];
  const cur = bank.base_currency;

  return (
    <Panel title="Products" sub="Each savings account and fixed deposit follows a product you define. Changes apply to savings from the next game day; fixed deposits keep their term's rate." flush
      actions={<span className="row" style={{ gap: 8 }}>
        <Button size="small" onClick={() => setEditing("fixed_deposit")}>Add fixed deposit</Button>
        <Button variant="primary" size="small" onClick={() => setEditing("savings")}>Add savings product</Button>
      </span>}>
      <ErrorNotice error={products.error} onRetry={products.reload} />
      {products.loading && !products.data && <Loading />}
      {products.data && rows.length === 0 && (
        <Empty title="No products yet" action={<Button variant="primary" onClick={() => setEditing("savings")}>Add savings product</Button>}>
          A savings product sets the interest and rules for savings accounts; a fixed-deposit product sets the terms you offer.
        </Empty>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Product</th><th>Rates</th><th>Pays</th><th>Rules</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-title">{p.name}</div>
                    <div className="cell-sub"><span className="mono">{p.code}</span> · {p.kind === "savings" ? "Savings" : "Fixed deposit"} · v{p.version}</div>
                  </td>
                  <td style={{ maxWidth: 260 }}>{describeRates(p, cur)}</td>
                  <td>
                    {p.kind === "savings" ? (p.payout_frequency === "quarterly" ? "Quarterly" : "Monthly") : "At maturity"}
                    {p.withholding_tax_bps > 0 && <div className="cell-sub">{pct(p.withholding_tax_bps)} tax withheld</div>}
                  </td>
                  <td className="small">
                    {p.kind === "savings" ? (
                      <>
                        <div>{describeWithdrawals(p)}</div>
                        {p.min_balance_minor > 0 && <div className="cell-sub">Keep {formatMoney(p.min_balance_minor, cur)}</div>}
                      </>
                    ) : (
                      <>
                        <div>{p.min_opening_minor > 0 ? `From ${formatMoney(p.min_opening_minor, cur)}` : "Any amount"}</div>
                        <div className="cell-sub">Early break: lose {pct(p.early_forfeit_bps)} of interest, {formatMoney(p.early_penalty_fixed_minor, cur)} + {pct(p.early_penalty_bps)} penalty</div>
                      </>
                    )}
                  </td>
                  <td><span className={`badge ${p.status === "active" ? "ok" : "bad"}`}>{p.status === "active" ? "Active" : "Inactive"}</span></td>
                  <td className="num"><Button size="small" onClick={() => setEditing(p)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ProductModal api={api} bank={bank} cur={cur} product={typeof editing === "string" ? null : editing} kind={typeof editing === "string" ? editing : editing.kind}
          onClose={() => setEditing(null)}
          onSaved={(p) => { setEditing(null); invalidate(key); toast(typeof editing === "string" ? `${p.name} added` : "Product saved"); }} />
      )}
    </Panel>
  );
}

const toCode = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "p_$1").slice(0, 63);

interface Row { a: string; b: string }

function ProductModal({ api, bank, cur, product, kind, onClose, onSaved }: {
  api: GameApi; bank: Bank; cur: CurrencyLike; product: DepositProduct | null; kind: "savings" | "fixed_deposit";
  onClose: () => void; onSaved: (p: DepositProduct) => void;
}) {
  const savings = kind === "savings";
  const major = (v: number | null | undefined) => (v == null ? "" : String(v / 10 ** cur.exponent));
  const pctIn = (bps: number | undefined) => (bps == null ? "" : String(bps / 100));
  const [name, setName] = useState(product?.name ?? "");
  const [tiers, setTiers] = useState<Row[]>(product?.rate_tiers.map((t) => ({ a: major(t.from_minor), b: pctIn(t.rate_bps) })) ?? [{ a: "0", b: "3" }]);
  const [terms, setTerms] = useState<Row[]>(product?.terms.map((t) => ({ a: String(t.days), b: pctIn(t.rate_bps) })) ?? [{ a: "30", b: "6" }, { a: "90", b: "9" }]);
  const [method, setMethod] = useState(product?.tier_method ?? "whole_balance");
  const [payout, setPayout] = useState<"monthly" | "quarterly">(product?.payout_frequency === "quarterly" ? "quarterly" : "monthly");
  const [tax, setTax] = useState(pctIn(product?.withholding_tax_bps ?? 0));
  const [minBal, setMinBal] = useState(major(product?.min_balance_minor || null));
  const [maxBal, setMaxBal] = useState(major(product?.max_balance_minor));
  const [perMonth, setPerMonth] = useState(product?.withdrawals_per_month != null ? String(product.withdrawals_per_month) : "");
  const [action, setAction] = useState(product?.excess_withdrawal_action ?? "block");
  const [minOpen, setMinOpen] = useState(major(product?.min_opening_minor || null));
  const [forfeit, setForfeit] = useState(pctIn(product?.early_forfeit_bps ?? 10_000));
  const [penFixed, setPenFixed] = useState(major(product?.early_penalty_fixed_minor || null));
  const [penPct, setPenPct] = useState(pctIn(product?.early_penalty_bps ?? 0));
  const [maturity, setMaturity] = useState<MaturityInstruction>(product?.default_maturity_instruction ?? "payout");
  const [active, setActive] = useState(product ? product.status === "active" : true);

  const money = (v: string) => (v.trim() ? parseMoney(v, cur.exponent) ?? 0 : 0);
  const bpsOf = (v: string) => (v.trim() ? parseMoney(v, 2) ?? 0 : 0);
  const save = useAction(() => {
    const body: DepositProductInput = { name: name.trim(), withholding_tax_bps: bpsOf(tax) };
    const clear: DepositProductInput["clear"] = [];
    if (savings) {
      body.rate_tiers = tiers.map((r) => ({ from_minor: money(r.a), rate_bps: bpsOf(r.b) }));
      body.tier_method = method;
      body.payout_frequency = payout;
      body.min_balance_minor = money(minBal);
      body.excess_withdrawal_action = action;
      if (maxBal.trim()) body.max_balance_minor = money(maxBal); else if (product) clear.push("max_balance_minor");
      if (perMonth.trim()) body.withdrawals_per_month = Number(perMonth); else if (product) clear.push("withdrawals_per_month");
    } else {
      body.terms = terms.map((r) => ({ days: Number(r.a), rate_bps: bpsOf(r.b) }));
      body.min_opening_minor = money(minOpen);
      body.early_forfeit_bps = bpsOf(forfeit);
      body.early_penalty_fixed_minor = money(penFixed);
      body.early_penalty_bps = bpsOf(penPct);
      body.default_maturity_instruction = maturity;
    }
    if (clear.length) body.clear = clear;
    if (product) {
      body.status = active ? "active" : "inactive";
      return api.updateDepositProduct(bank.id, product.id, body);
    }
    return api.createDepositProduct(bank.id, { ...body, code: toCode(name), kind });
  });

  const rows = (list: Row[], set: (r: Row[]) => void, a: { label: string; before?: string; after?: string; placeholder: string }) => (
    <div className="stack" style={{ gap: 8 }}>
      {list.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto", gap: 8, alignItems: "end" }}>
          <Field label={i === 0 ? a.label : ""}>
            <Affix before={a.before} after={a.after}>
              <Input inputMode="decimal" value={r.a} onChange={(e) => set(list.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} placeholder={a.placeholder} required />
            </Affix>
          </Field>
          <Field label={i === 0 ? "Annual rate" : ""}>
            <Affix after="%"><Input inputMode="decimal" value={r.b} onChange={(e) => set(list.map((x, j) => (j === i ? { ...x, b: e.target.value } : x)))} placeholder="3" required /></Affix>
          </Field>
          {list.length > 1 ? <Button size="small" onClick={() => set(list.filter((_, j) => j !== i))} aria-label="Remove row" style={{ marginBottom: 6 }}>Remove</Button> : <span />}
        </div>
      ))}
      <div><Button size="small" onClick={() => set([...list, { a: "", b: "" }])}>Add {savings ? "tier" : "term"}</Button></div>
    </div>
  );

  return (
    <Modal open size="wide" title={product ? `Edit ${product.name}` : savings ? "Add a savings product" : "Add a fixed-deposit product"} onClose={onClose}
      description={<>Amounts in {cur.code}; rates are annual and accrue every game day.</>}
      onSubmit={async (e) => { e.preventDefault(); const p = await save.run(); if (p) onSaved(p); }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={save.pending} disabled={!name.trim()}>{product ? "Save" : "Add product"}</Button></>}>
      <div className="form">
        <section className="form-section">
          <h3>Product</h3>
          <Field label="Name" hint={product ? undefined : name ? <>Code <span className="mono">{toCode(name)}</span></> : "Shown to players, e.g. Easy Saver."}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required autoFocus placeholder={savings ? "Easy Saver" : "Term Deposit"} />
          </Field>
        </section>

        {savings ? (
          <>
            <section className="form-section">
              <h3>Interest <InfoTip>Interest accrues every game day on the account's closing balance and is paid at the end of each month or quarter.</InfoTip></h3>
              {rows(tiers, setTiers, { label: "From a balance of", before: cur.symbol ?? cur.code, placeholder: "0" })}
              {tiers.length > 1 && (
                <div className="field">
                  <span>Tiers apply to<InfoTip>Whole balance: the highest tier reached sets the rate for all of it. Banded: each band of the balance earns its own rate, like tax bands.</InfoTip></span>
                  <Segmented label="Tier method" value={method} onChange={setMethod}
                    options={[{ value: "whole_balance", label: "The whole balance" }, { value: "banded", label: "Each band" }]} />
                </div>
              )}
              <div className="form-row">
                <div className="field">
                  <span>Paid</span>
                  <Segmented label="Payout" value={payout} onChange={setPayout} options={[{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }]} />
                </div>
                <Field label="Withholding tax" tip="Deducted from each payout and credited to the bank's levies payable account.">
                  <Affix after="%"><Input inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0" /></Affix>
                </Field>
              </div>
            </section>
            <section className="form-section">
              <h3>Rules</h3>
              <div className="form-row">
                <Field label="Minimum balance" hint="Withdrawals can't go below it. Empty for none.">
                  <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={minBal} onChange={(e) => setMinBal(e.target.value)} placeholder="None" /></Affix>
                </Field>
                <Field label="Maximum balance" hint="Payments in can't take it over. Empty for none.">
                  <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={maxBal} onChange={(e) => setMaxBal(e.target.value)} placeholder="None" /></Affix>
                </Field>
              </div>
              <div className="form-row">
                <Field label="Withdrawals a month" hint="Empty for unlimited.">
                  <Input type="number" min={0} value={perMonth} onChange={(e) => setPerMonth(e.target.value)} placeholder="Unlimited" />
                </Field>
                <Field label="After that" tip="Block refuses the withdrawal. Forfeit lets it through but the period's interest is lost. Fee charges your “Extra savings withdrawal” fee rule.">
                  <Select value={action} onChange={(e) => setAction(e.target.value as DepositProduct["excess_withdrawal_action"])} disabled={!perMonth.trim()}>
                    <option value="block">Block the withdrawal</option>
                    <option value="forfeit_interest">Allow, lose the interest</option>
                    <option value="fee">Allow, charge a fee</option>
                  </Select>
                </Field>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="form-section">
              <h3>Terms <InfoTip>The terms players can choose. A deposit keeps the rate it started with until its term ends.</InfoTip></h3>
              {rows(terms, setTerms, { label: "Term", after: "game days", placeholder: "90" })}
              <div className="form-row">
                <Field label="Minimum deposit">
                  <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={minOpen} onChange={(e) => setMinOpen(e.target.value)} placeholder="None" /></Affix>
                </Field>
                <Field label="At maturity, by default">
                  <Select value={maturity} onChange={(e) => setMaturity(e.target.value as MaturityInstruction)}>
                    {INSTRUCTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                  </Select>
                </Field>
                <Field label="Withholding tax">
                  <Affix after="%"><Input inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0" /></Affix>
                </Field>
              </div>
            </section>
            <section className="form-section">
              <h3>Breaking early</h3>
              <div className="form-row">
                <Field label="Interest lost" tip="The share of the interest earned so far that the player gives up.">
                  <Affix after="%"><Input inputMode="decimal" value={forfeit} onChange={(e) => setForfeit(e.target.value)} placeholder="100" /></Affix>
                </Field>
                <Field label="Penalty" tip="A fixed amount plus a percentage of the principal, credited to the bank's fee income.">
                  <Affix before={cur.symbol ?? cur.code}><Input inputMode="decimal" value={penFixed} onChange={(e) => setPenFixed(e.target.value)} placeholder="0" /></Affix>
                </Field>
                <Field label="+ of the principal">
                  <Affix after="%"><Input inputMode="decimal" value={penPct} onChange={(e) => setPenPct(e.target.value)} placeholder="0" /></Affix>
                </Field>
              </div>
            </section>
          </>
        )}
        {product && (
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active <span className="cell-sub">An inactive product opens no new accounts or deposits; existing ones keep earning.</span></span>
          </label>
        )}
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}
